import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { getModuleContentAnalysis } from '../../../../Api/moduleModeratorApi'
import { FaBook, FaQuestionCircle, FaFileAlt, FaLayerGroup, FaUser, FaCalendar, FaTag } from 'react-icons/fa'
import './ModuleContentAnalysis.scss'

const ModuleContentAnalysis = ({ moduleId, onClose }) => {
  const { data: analysis, isLoading, error } = useQuery(
    ['moduleContentAnalysis', moduleId],
    () => getModuleContentAnalysis(moduleId),
    {
      enabled: !!moduleId,
    }
  );

  if (isLoading) {
    return (
      <div className="module-analysis-modal">
        <div className="modal-overlay" onClick={onClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="loading-content">Loading module analysis...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="module-analysis-modal">
        <div className="modal-overlay" onClick={onClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="error-content">Failed to load module analysis</div>
          </div>
        </div>
      </div>
    );
  }

  if (!analysis?.payload) {
    return null;
  }

  const { module, contentAnalysis } = analysis.payload;

  return (
    <div className="module-analysis-modal">
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content module-analysis-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Module Content Analysis</h3>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>

          <div className="analysis-content">
            {/* Module Information */}
            <div className="module-info">
              <h4 className="module-title">{module.title}</h4>
              <div className="module-meta">
                <div className="meta-item">
                  <FaUser className="meta-icon" />
                  <span>{module.moderator.name}</span>
                </div>
                <div className="meta-item">
                  <FaTag className="meta-icon" />
                  <span>Grade {module.grade}</span>
                </div>
                <div className="meta-item">
                  <FaCalendar className="meta-icon" />
                  <span>{new Date(module.createdAt).toLocaleDateString()}</span>
                </div>
                <div className={`status-badge status-${module.status}`}>
                  {module.status}
                </div>
                <div className={`type-badge ${module.isFree ? 'free' : 'paid'}`}>
                  {module.isFree ? 'Free' : 'Paid'}
                </div>
              </div>
              {module.description && (
                <p className="module-description">{module.description}</p>
              )}
            </div>

            {/* Content Statistics */}
            <div className="content-stats">
              <h5>Content Overview</h5>
              <div className="stats-grid">
                <div className="stat-card">
                  <FaBook className="stat-icon" />
                  <div className="stat-content">
                    <div className="stat-value">{contentAnalysis.totalChapters}</div>
                    <div className="stat-label">Chapters</div>
                  </div>
                </div>
                <div className="stat-card">
                  <FaQuestionCircle className="stat-icon" />
                  <div className="stat-content">
                    <div className="stat-value">{contentAnalysis.totalQBanks}</div>
                    <div className="stat-label">Question Banks</div>
                  </div>
                </div>
                <div className="stat-card">
                  <FaFileAlt className="stat-icon" />
                  <div className="stat-content">
                    <div className="stat-value">{contentAnalysis.totalFiles}</div>
                    <div className="stat-label">Files</div>
                  </div>
                </div>
                <div className="stat-card">
                  <FaLayerGroup className="stat-icon" />
                  <div className="stat-content">
                    <div className="stat-value">{contentAnalysis.totalFlashcardSets}</div>
                    <div className="stat-label">Flashcard Sets</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Chapters Detail */}
            {contentAnalysis.chapters.length > 0 && (
              <div className="chapters-section">
                <h5>Chapters ({contentAnalysis.chapters.length})</h5>
                <div className="chapters-list">
                  {contentAnalysis.chapters.map((chapter) => (
                    <div key={chapter.id} className="chapter-item">
                      <div className="chapter-header">
                        <h6 className="chapter-title">{chapter.title}</h6>
                        <div className="chapter-date">
                          {new Date(chapter.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="chapter-content">
                        <div className="content-item">
                          <FaQuestionCircle className="content-icon" />
                          <span>{chapter.contentCount.qBanks} Question Banks</span>
                        </div>
                        <div className="content-item">
                          <FaFileAlt className="content-icon" />
                          <span>{chapter.contentCount.files} Files</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Flashcard Sets */}
            {contentAnalysis.flashcardSets.length > 0 && (
              <div className="flashcards-section">
                <h5>Flashcard Sets ({contentAnalysis.flashcardSets.length})</h5>
                <div className="flashcards-list">
                  {contentAnalysis.flashcardSets.map((set) => (
                    <div key={set.id} className="flashcard-item">
                      <div className="flashcard-title">{set.title}</div>
                      <div className="flashcard-date">
                        {new Date(set.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleContentAnalysis;
